import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Check } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SERVICE_OPTIONS, STATE_NAMES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATE_LIST = Object.entries(STATE_NAMES)
  .map(([abbr, name]) => ({ abbr, name }))
  .sort((a, b) => a.name.localeCompare(b.name));

const fieldCls =
  "w-full rounded-none border-0 border-b border-line bg-transparent px-0 py-3 text-navy outline-none transition-colors placeholder:text-slatey/60 focus:border-electric";

const empty = {
  first_name: "",
  last_name: "",
  company_name: "",
  email: "",
  phone: "",
  state: "",
  service_of_interest: "",
  message: "",
};

const Label = ({ children, required }) => (
  <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.16em] text-slatey">
    {children}
    {required && <span className="text-electric"> *</span>}
  </label>
);

export default function ContactForm({ defaultService, defaultState }) {
  const [form, setForm] = useState({
    ...empty,
    state: defaultState || "",
    service_of_interest: defaultService || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const validate = () => {
    const required = [
      "first_name",
      "last_name",
      "company_name",
      "email",
      "phone",
      "state",
      "service_of_interest",
      "message",
    ];
    for (const k of required) {
      if (!String(form[k]).trim()) {
        toast.error("Please complete all fields.");
        return false;
      }
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return false;
    }
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form, { timeout: 15000 });
      setDone(true);
      toast.success("Thank you — your inquiry has been received.");
      setForm({ ...empty });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="contact-success"
        className="flex flex-col items-start rounded-sm border border-line bg-ice p-10"
      >
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-energy/15 text-energy">
          <Check className="h-7 w-7" />
        </div>
        <h3 className="mt-6 font-heading text-2xl text-navy">Inquiry received</h3>
        <p className="mt-3 max-w-md text-slatey">
          Thank you for reaching out to Innervation Energy Group. A commercial
          energy advisor will be in touch shortly.
        </p>
        <button
          onClick={() => setDone(false)}
          data-testid="contact-reset"
          className="mt-8 font-mono text-xs uppercase tracking-[0.16em] text-electric hover:text-electric-hover"
        >
          Submit another inquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-8" noValidate>
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
        <div>
          <Label required>First Name</Label>
          <input
            data-testid="input-first-name"
            className={fieldCls}
            value={form.first_name}
            onChange={set("first_name")}
            placeholder="Jane"
          />
        </div>
        <div>
          <Label required>Last Name</Label>
          <input
            data-testid="input-last-name"
            className={fieldCls}
            value={form.last_name}
            onChange={set("last_name")}
            placeholder="Doe"
          />
        </div>
        <div>
          <Label required>Company Name</Label>
          <input
            data-testid="input-company"
            className={fieldCls}
            value={form.company_name}
            onChange={set("company_name")}
            placeholder="Acme Manufacturing"
          />
        </div>
        <div>
          <Label required>Email</Label>
          <input
            data-testid="input-email"
            type="email"
            className={fieldCls}
            value={form.email}
            onChange={set("email")}
            placeholder="jane@company.com"
          />
        </div>
        <div>
          <Label required>Phone</Label>
          <input
            data-testid="input-phone"
            className={fieldCls}
            value={form.phone}
            onChange={set("phone")}
            placeholder="(555) 000-0000"
          />
        </div>
        <div>
          <Label required>State</Label>
          <Select value={form.state} onValueChange={set("state")}>
            <SelectTrigger
              data-testid="select-state"
              className="h-auto rounded-none border-0 border-b border-line bg-transparent px-0 py-3 text-navy focus:ring-0 focus:ring-offset-0 data-[placeholder]:text-slatey/60"
            >
              <SelectValue placeholder="Select a state" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {STATE_LIST.map((s) => (
                <SelectItem key={s.abbr} value={s.name}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label required>Service of Interest</Label>
        <Select
          value={form.service_of_interest}
          onValueChange={set("service_of_interest")}
        >
          <SelectTrigger
            data-testid="select-service"
            className="h-auto rounded-none border-0 border-b border-line bg-transparent px-0 py-3 text-navy focus:ring-0 focus:ring-offset-0 data-[placeholder]:text-slatey/60"
          >
            <SelectValue placeholder="Select a service" />
          </SelectTrigger>
          <SelectContent>
            {SERVICE_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label required>Message</Label>
        <textarea
          data-testid="input-message"
          rows={4}
          className={`${fieldCls} resize-none`}
          value={form.message}
          onChange={set("message")}
          placeholder="Tell us about your facilities and what you'd like help with."
        />
      </div>

      <button
        type="submit"
        data-testid="contact-submit"
        disabled={submitting}
        className="inline-flex items-center gap-2 bg-electric px-8 py-4 text-sm font-medium text-white transition-transform hover:scale-[0.98] hover:bg-electric-hover disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? "Sending…" : "Send Inquiry"}
        <ArrowUpRight className="h-4 w-4" />
      </button>
    </form>
  );
}
